import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpService } from '@app/@core/services/http.service';
import { LocalService } from '@app/@core/services/local.service';
import { DataStoreService } from '@pics-core/dynamic';
import { forkJoin, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs';
import { CommonService } from '@app/shared/services/common.service';
import { DashboardAdmin } from '@app/shared/directives/queue-status-filter.constant';
import { CommonModule } from '@angular/common';

interface TimelineItem {
  status: string;
  date: string;
  name: string;
}

interface ApiTimelineItem {
  timeline_event_id: number;
  application_id: number;
  cd_application_status: string;
  cd_bgc_status: string;
  bg_required: boolean;
  occurred_date: string;
  person_id: number;
  created_date: string;
  created_by: number;
  updated_date: string;
  updated_by: number;
  is_active: boolean;
  old_id: string;
}

@Component({
  selector: 'app-surrender-timeline',
  templateUrl: './surrender-timeline.component.html',
  styleUrls: ['./surrender-timeline.component.scss'],
  standalone: true,
  imports :[CommonModule]
})
export class SurrenderTimelineComponent implements OnInit, OnDestroy {
  
  timelineData: TimelineItem[] = [];
  applicationStatusOptions: any[] = [];
  backgroundStatusOptions: any[] = [];
  personData: any = null;
  userData: any = null;
  applicationID: any = null;
  userID: any = null;
  createdByUser: any = null;
  parentGridPageId: any;
  injector: Injector;
  dataStore: DataStoreService;
  hidePreviousNextButtons: boolean = false;
  private userCache: Map<number, any> = new Map();
  private destroy$ = new Subject<void>();
  constructor(
    private readonly httpService: HttpService,
    private readonly sessionStorage: LocalService,
    public router: Router,
    injector: Injector,
    private readonly route: ActivatedRoute,
    private readonly commonservice: CommonService
  ) {
     this.injector = injector;
     this.dataStore = injector.get<DataStoreService>(DataStoreService);
      this.checkReadOnlyModeToHidePreviousNextButton();
   }

  ngOnInit(): void {
    this.getPersonDataFromSession();
    this.getApplicationIDFromSession();
    this.loadData();
    this.checkAndUpdateNavigationData();
  }

  private loadData(): void {
    if (!this.applicationID) {
      console.error('Application ID not found in session storage');
      this.timelineData = [];
      return;
    }

    console.log('Loading application timeline data for applicationID:', this.applicationID);

    // Load both application status options and timeline data simultaneously
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=APPLICATION_STATUS'),
      this.httpService.get(`/application-flow/appTimelineData?applicationId=${this.applicationID}`),
      this.httpService.get(`/domain/lookup?requestType=BG_STATUS`)
    ]).subscribe({
      next: (results: any[]) => {
        // Load application status options from first response
        this.applicationStatusOptions = results[0].data;
        console.log('Application status options loaded:', this.applicationStatusOptions);
        this.backgroundStatusOptions = results[2].data;
        // Process timeline data from second response
        const timelineResponse = results[1];
        console.log('Application timeline response:', timelineResponse);

        if (timelineResponse.status === 'success' && timelineResponse.data?.data) {
          this.processTimelineData(
            Array.isArray(timelineResponse.data.data) ? timelineResponse.data.data : [timelineResponse.data.data]
          );
        } else {
          console.warn('No timeline data found or invalid response structure');
          this.timelineData = [];
        }
      },
      error: error => {
        console.error('Error loading application timeline data:', error);
        this.timelineData = [];
      }
    });
  }

  private getPersonDataFromSession(): void {
    const personDataString = this.sessionStorage.getItem('personData');
    this.personData = personDataString ? JSON.parse(personDataString) : null;
  }

  private getApplicationIDFromSession(): void {
    const applicationIDString = this.sessionStorage.getItem('applicationID');
    this.applicationID = applicationIDString ? JSON.parse(applicationIDString) : null;
  }

  private processTimelineData(apiData: ApiTimelineItem[]): void {
    console.log('Processing application timeline data:', apiData);

    // Sort by occurred_date in descending order (newest first/top, oldest last/bottom)
    const sortedData = apiData.sort((a, b) => {
      const dateA = new Date(a.occurred_date).getTime();
      const dateB = new Date(b.occurred_date).getTime();
      return dateB - dateA; // Descending order: newest first, oldest last
    });

    // Fetch user data for all unique users and then process timeline
    const uniqueUserIds = [...new Set(sortedData.map(item => item.created_by))];
    this.fetchUserDataForIds(uniqueUserIds).then(() => {
      this.timelineData = sortedData.map(item => {
        const statusLabel = this.getStatusLabel(item.cd_application_status);
        const backgroundStatusLabel = this.getBackgroundStatusDisplayValue(item.cd_bgc_status);
        const combinedStatus = this.getCombinedStatus(item.cd_application_status, backgroundStatusLabel, item.bg_required);
        const formattedDate = this.formatDate(item.occurred_date);
        const personName = this.getPersonName(item.created_by);

        return {
          status: this.toTitleCase(combinedStatus),
          date: formattedDate,
          name: this.toTitleCase(personName)
        };
      });

      console.log('Processed application timeline data:', this.timelineData);
      
      // After timeline data is processed, check and update navigation data if needed
      this.checkAndUpdateNavigationData();
    });
  }

  private getBackgroundStatusDisplayValue(key: string): string {
     if (!this.backgroundStatusOptions || !key) {
      return key || '';
    }
    const bgStatus = this.backgroundStatusOptions.find(status => status.key === key);
    return bgStatus ? bgStatus.value : key;
  }

  private getCombinedStatus(appStatus: string, bgStatus: string, bgRequired: boolean): string {
    const rules = DashboardAdmin
    let applicationStatus = '';
    rules.some(rule => {
        if (rule.appStatus !== appStatus) {
          applicationStatus = this.getApplicationStatusDisplayValue(appStatus);
          return false;
        }
        if(bgRequired === true){
          if (rule.bgStatus.includes('-')) {
            applicationStatus = rule.applicationStatus;
            return true;
          }
          if(rule.bgStatus.includes(bgStatus) || rule.bgStatus.length === 0){
            applicationStatus = rule.applicationStatus;
            return  true;
          }
        }else{
           if (!rule.bgStatus || rule.bgStatus.length === 0) {
              applicationStatus = rule.applicationStatus;
              return true;
            }
        }
      });
    return applicationStatus;
  }

  private getStatusLabel(statusCode: string): string {
    return this.getApplicationStatusDisplayValue(statusCode);
  }

  getApplicationStatusDisplayValue(key: string): string {
    if (!this.applicationStatusOptions || !key) {
      return key || '';
    }
    
    const appStatus = this.applicationStatusOptions.find(status => status.key === key);
    return appStatus ? (appStatus.value) : key;
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    return `${month} ${day}, ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
  }

  private getPersonName(userID: number): string {
    const userData = this.userCache.get(userID);

    if (!userData) {
      return 'Unknown User';
    }

    const firstName = userData.first_name || '';
    const lastName = userData.last_name || '';

    return `${firstName} ${lastName}`.trim() || 'Unknown User';
  }

  private toTitleCase(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  private fetchUserDataForIds(userIds: number[]): Promise<void> {
    const requests = userIds
      .filter(id => !this.userCache.has(id)) // Only fetch if not already cached
      .map(id =>
        this.httpService
          .get(`/application-flow/person?userId=${id}`)
          .toPromise()
          .then((response: any) => {
            if (response?.data) {
              this.userCache.set(id, response.data[0]);
            }
          })
          .catch((error: any) => {
            console.error(`Error fetching user data for userId ${id}:`, error);
            this.userCache.set(id, { first_name: 'Unknown', last_name: 'User' });
          })
      );

    return Promise.all(requests).then(() => {});
  }

  isSingleItem(): boolean {
    return this.timelineData.length === 1;
  }

  moveToNext(){
    const tabList = JSON.parse(this.dataStore.getData('TABCONFIGLIST') || '[]');
    const currentTabIndex = tabList?.findIndex(tab => tab.id == 3680);
    const parentGridPageInfo = this.sessionStorage.getObj('backToGridPage') ? this.sessionStorage.getObj('backToGridPage') : this.sessionStorage.getObj('parentGridPage') ? this.sessionStorage.getObj('parentGridPage') : {};
      let nextTab = null;
      let islastTab = null;
      if (currentTabIndex !== -1 && currentTabIndex == tabList.length - 1) {
        islastTab = tabList[currentTabIndex + 1] ? false : true;
      }
      currentTabIndex !== -1 ? nextTab = tabList[currentTabIndex + 1] : null;
      let lastTabId = null
      if (islastTab) {
        lastTabId = tabList[currentTabIndex]?.id;
      }
      const nextTabIndex = currentTabIndex + 1;
      if (islastTab) {
        if (parentGridPageInfo.id) {
        this.router.navigate([`/pages/dynamic-search/search/${parentGridPageInfo?.id}`], { relativeTo: this.route });
        } else {
          this.router.navigate([`/pages/${parentGridPageInfo.name}`], { relativeTo: this.route });
        }
      } else if (nextTab && nextTab.id) {
        this.dataStore.setData('FORM_SUBMISSION_SUCCESS', { isRoute: true, nextPage: nextTabIndex, submissionId: null });
        return
      }
      else {
       // this.location.back();
        return;
      }
  }
  moveToPrevious(){
    const tabList = JSON.parse(this.dataStore.getData('TABCONFIGLIST') || '[]');
    const currentTabIndex = tabList?.findIndex(tab => tab.id == 3752);
    const parentGridPageInfo = this.sessionStorage.getObj('backToGridPage') ? this.sessionStorage.getObj('backToGridPage') : this.sessionStorage.getObj('parentGridPage') ? this.sessionStorage.getObj('parentGridPage') : {};
      let previousTab = null;
      let isFirstTab = null;
      if (currentTabIndex === 0) {
        isFirstTab = true;
      }
      currentTabIndex > 0 ? previousTab = tabList[currentTabIndex - 1] : null;
      const previousTabIndex = currentTabIndex - 1;
      if (isFirstTab) {
        if (parentGridPageInfo.id) {
        this.router.navigate([`/pages/dynamic-search/search/${parentGridPageInfo?.id}`], { relativeTo: this.route });
        } else {
          this.router.navigate([`/pages/${parentGridPageInfo.name}`], { relativeTo: this.route });
        }
      } else if (previousTab && previousTab.id) {
        this.dataStore.setData('FORM_SUBMISSION_SUCCESS', { isRoute: true, nextPage: previousTabIndex, submissionId: null });
        return
      }
      else {
        return;
      }
  }

  private checkAndUpdateNavigationData(): void {
    const navigationData = this.sessionStorage.getObj('navigationData');
    
    if (!navigationData || !navigationData.bannerheader) {
      console.log('No navigation data found in session');
      return;
    }

    const applicationType = navigationData.bannerheader['Application Type'];
    const applicationStatus = JSON.parse(this.sessionStorage.getItem('applicationStatus') || 'null');
    
    // Check if application type is Name Change and status is In progress or Submitted
    const isNameChange = applicationType && applicationType.toLowerCase().includes('name change');
    const isInProgressOrSubmitted = applicationStatus && 
      (applicationStatus.toLowerCase().includes('in progress, not submitted') );
    
    if (isNameChange) {
      console.log('Name Change application with In Progress/Submitted status detected, updating navigation data');
      this.loadApplicationStatusAndSetNavigation();
    }
  }

  private loadApplicationStatusAndSetNavigation(): void {
    const applicationId = this.sessionStorage.getItem('applicationID');
    if (!applicationId) {
      console.error('No application ID found in session storage');
      return;
    }

    const userId = this.sessionStorage.getItem('id');
    if (!userId) {
      console.error('No user ID found in session storage');
      return;
    }

    // Call API to get all applications for the user
    this.httpService.post('/registration/applications', { userId: userId })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && response.data && Array.isArray(response.data.data)) {
            // Find the current application by ID
            const currentApplication = response.data.data.find(
              (app: any) => String(app.id) === String(applicationId) || String(app.application_id) === String(applicationId)
            );

            if (currentApplication) {
              // Get the latest status from timeline data
              if (this.timelineData && this.timelineData.length > 0) {
                const latestStatus = this.timelineData[0].status; // First item is the latest due to descending sort
                
                const existingNavigationData = this.sessionStorage.getObj('navigationData');
                let navigationData = existingNavigationData || {};
                
                if (!navigationData.bannerheader) {
                  navigationData.bannerheader = {};
                }
                
                // Update application status with latest from timeline
                // navigationData.bannerheader['Application Status'] = latestStatus;
                
                // Set Submitted Date from created_date if it exists
                if (currentApplication.created_date) {
                  navigationData.bannerheader['Submitted Date'] = this.commonservice.getFormatedDate(currentApplication.created_date);
                  
                  // Remove Created Date if Submitted Date is being added
                  if (navigationData.bannerheader['Created Date']) {
                    delete navigationData.bannerheader['Created Date'];
                  }
                }
                
                this.sessionStorage.setObj('navigationData', navigationData);
                
                console.log('Navigation data updated for Name Change application:', navigationData);
              } else {
                console.log('Timeline data not yet loaded, will update after timeline data loads');
                // Wait for timeline data to load, then update
                setTimeout(() => this.updateNavigationWithLatestTimeline(currentApplication), 500);
              }
            } else {
              console.warn('Current application not found in the response');
            }
          } else {
            console.warn('Invalid response format from applications API');
          }
        },
        error: (error) => {
          console.error('Error fetching application data:', error);
        }
      });
  }

  private updateNavigationWithLatestTimeline(currentApplication: any): void {
    if (this.timelineData && this.timelineData.length > 0) {
      const latestStatus = this.timelineData[0].status;
      
      const existingNavigationData = this.sessionStorage.getObj('navigationData');
      let navigationData = existingNavigationData || {};
      
      if (!navigationData.bannerheader) {
        navigationData.bannerheader = {};
      }
      
      // navigationData.bannerheader['Application Status'] = latestStatus;
      
      if (currentApplication.created_date) {
        navigationData.bannerheader['Submitted Date'] = this.commonservice.getFormatedDate(currentApplication.created_date);
        
        if (navigationData.bannerheader['Created Date']) {
          delete navigationData.bannerheader['Created Date'];
        }
      }
      
      this.sessionStorage.setObj('navigationData', navigationData);
      
      console.log('Navigation data updated with timeline status:', navigationData);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkReadOnlyModeToHidePreviousNextButton() {
    let appMode = JSON.parse(this.sessionStorage.getItem('applicationmode'));
    let isReadOnlyMode = false;
    if (appMode === 'view') {
      isReadOnlyMode = true;
    }
    if (isReadOnlyMode) {
      this.hidePreviousNextButtons = true;
    }
  }
}

